/* Store locator (Store Platform P8 + P9, frontend contract V1). Store identity and operational presentation come from
   the normalized BB.stores contract; the Liquid blocks only declare which slugs/cards belong on this surface.
   - On load: one cached BB.stores.fetchList(context) supplies store identity, operational data, coordinates and P8
     productMatch passthrough. Before any search the cards keep their declared order except the existing evidence order.
   - Search (text) and "use my location" (browser geolocation, only after a click) go to POST /public/v1/store-locator/search.
     The platform resolves the location, computes `distanceKm` and returns the stores server-ranked (evidence class →
     distance → name → slug). The theme takes that order 1:1 (DOM re-ordering is presentation, not ranking), shows the
     server distance and never computes or sorts distances itself. Unmatched platform stores are counted, never rendered.
   - Failure policy: 422 location_unresolved → "no location found", the list stays; 503 / network / timeout → "search
     temporarily unavailable", the full SSR list stays usable (no local matching, no fake precision); 429 → "try again
     later". Geolocation denied/unavailable → text search stays available, never a second prompt.
   Privacy: query text and coordinates go in the POST body only (never URL, storage or analytics); `store_locator_search`
   carries `query_type` and `results_count` only. The map mount stays inert (addendum H, later). Business truth = platform. */
(function () {
  'use strict';
  const BB = window.BB;
  const SEARCH_PATH = '/public/v1/store-locator/search';

  /* PDP → locator handoff (?productGid=&brand=): the canonical Shopify product GID and the vendor string, exactly as the
     P8 contract takes them. A handle is never accepted or converted (no inference). */
  function urlContext() {
    try {
      const q = new URLSearchParams(location.search);
      return BB.showroom.context({ productGid: q.get('productGid'), brand: q.get('brand') });
    } catch (e) { return {}; }
  }
  function uniqueShowroomHandoff() {
    try { return new URLSearchParams(location.search).get('uniqueShowroom') === '1'; }
    catch (e) { return false; }
  }

  /* ---- pure helpers (also exercised by tests/) ------------------------------------------------------------------ */

  /* P9 request body: exactly one location mode plus the P8 context (same keys/values as the GET enrichment). */
  function buildBody(location, ctx) {
    const body = { location: {} };
    if (location && typeof location.query === 'string') body.location.query = location.query;
    else if (location && typeof location.latitude === 'number' && typeof location.longitude === 'number') { body.location.latitude = location.latitude; body.location.longitude = location.longitude; }
    const c = BB.showroom.context(ctx || {});
    if (c.productGid) body.productGid = c.productGid;
    if (c.brand) body.brand = c.brand;
    return body;
  }

  /* Maps a fetch failure to one of the locator's public outcomes (LOCATOR_ERROR_CODES) — never a raw code for the UI. */
  function classifyError(err) {
    const code = err && err.body && err.body.error && err.body.error.code;
    const status = err && err.status;
    if (status === 422) return code === 'location_required' ? 'location_required' : 'location_unresolved';
    if (status === 429 || code === 'rate_limited') return 'rate_limited';
    if (status === 400) return 'malformed_request';
    return 'unavailable'; /* 503 location_search_unavailable, 500, network error, timeout (AbortError), CORS */
  }

  /* Server order → DOM order: matched cards in exactly the server sequence, then the unmatched SSR cards in their own
     order. Returns { ordered, matched, unmatchedStores } — unmatched platform stores are reported, never rendered. */
  function orderBySlugs(cards, stores) {
    const bySlug = new Map(cards.map((c) => [c.getAttribute('data-store-slug'), c]));
    const ordered = [];
    const unmatchedStores = [];
    (stores || []).forEach((s) => {
      const card = s && typeof s.slug === 'string' ? bySlug.get(s.slug) : null;
      if (card && ordered.indexOf(card) === -1) ordered.push(card); else if (s && s.slug) unmatchedStores.push(s.slug);
    });
    const matched = ordered.length;
    cards.forEach((c) => { if (ordered.indexOf(c) === -1) ordered.push(c); });
    return { ordered, matched, unmatchedStores };
  }

  /* `distanceKm` from the platform: a number (one decimal) → localized text; null/invalid → null (unknown, not 0). */
  function formatDistance(km) {
    if (typeof km !== 'number' || !isFinite(km) || km < 0) return null;
    let text;
    try { text = km.toLocaleString((BB.config && BB.config.locale) || document.documentElement.lang || 'nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 1 }); } catch (e) { text = String(Math.round(km * 10) / 10); }
    return BB.t('distance', { km: text });
  }

  const openState = (store) => BB.stores.format.openState(store);

  function applyOpenState(el, store) {
    const st = openState(store);
    if (!el) return st;
    el.classList.remove('is-open', 'is-closed');
    if (st.state !== 'open' && st.state !== 'closed') { el.hidden = true; el.textContent = ''; return st; }
    el.textContent = st.text;
    el.classList.add('is-' + st.state);
    el.hidden = false;
    return st;
  }

  function setLink(link, href) {
    if (!link) return;
    if (href) { link.setAttribute('href', href); link.hidden = false; }
    else { link.removeAttribute('href'); link.hidden = true; }
  }

  function cardLinks(card) {
    return {
      page: card.querySelector('[data-store-page-link]'),
      route: card.querySelector('[data-bb-store-route]'),
      phone: card.querySelector('[data-bb-store-call]'),
      email: card.querySelector('[data-store-email]')
    };
  }

  function resetIdentity(card) {
    const generic = card.getAttribute('data-store-generic-name') || '';
    const title = card.querySelector('[data-store-name]');
    if (title) title.textContent = generic;
    const image = card.querySelector('[data-store-image] img');
    if (image) image.alt = generic;
    const appointment = card.querySelector('[data-bb-appointment]');
    if (appointment) appointment.removeAttribute('data-store-name');
  }

  function applyAuthorityState(card, state) {
    card.setAttribute('data-store-source', state);
    card.setAttribute('data-store-content', 'missing');
    resetIdentity(card);
    const address = card.querySelector('[data-store-address]');
    const status = card.querySelector('[data-store-status]');
    const hours = card.querySelector('[data-store-hours-fallback]');
    const distance = card.querySelector('[data-store-distance]');
    const links = cardLinks(card);
    if (address) { address.textContent = ''; address.hidden = true; }
    if (status) { status.textContent = ''; status.classList.remove('is-open', 'is-closed'); status.hidden = true; }
    if (hours) { hours.textContent = BB.stores.format.resultState({ state: state }).text; hours.hidden = false; }
    if (distance) { distance.textContent = ''; distance.hidden = true; }
    setLink(links.page, null); setLink(links.route, null); setLink(links.phone, null); setLink(links.email, null);
    const select = card.querySelector('[data-store-select]');
    if (select) select.hidden = true;
  }

  function applyOperationalCard(card, store) {
    card.setAttribute('data-store-source', BB.stores.states.PLATFORM);
    card.setAttribute('data-store-content', store.projectionState || 'missing');
    const title = card.querySelector('[data-store-name]');
    const addressEl = card.querySelector('[data-store-address]');
    const statusEl = card.querySelector('[data-store-status]');
    const hoursEl = card.querySelector('[data-store-hours-fallback]');
    const links = cardLinks(card);
    const address = BB.stores.format.address(store.address);
    const state = applyOpenState(statusEl, store);

    const generic = card.getAttribute('data-store-generic-name') || '';
    if (title) title.textContent = store.name || generic;
    const image = card.querySelector('[data-store-image] img');
    if (image) image.alt = store.name || generic;
    if (addressEl) { addressEl.textContent = address.singleLine || ''; addressEl.hidden = !address.singleLine; }

    if (hoursEl) {
      if (state.state === 'unknown') { hoursEl.textContent = state.text; hoursEl.hidden = false; }
      else { hoursEl.textContent = ''; hoursEl.hidden = true; }
    }

    setLink(links.page, store.shopifyPageUrl);
    setLink(links.route, BB.stores.format.routeUrl(store));
    const phoneHref = BB.stores.format.telHref(store);
    setLink(links.phone, phoneHref);
    const phoneText = links.phone && links.phone.querySelector('[data-store-phone-text]');
    if (phoneText) phoneText.textContent = phoneHref && store.contact && store.contact.phone ? store.contact.phone : '';
    const emailHref = BB.stores.format.emailHref(store);
    setLink(links.email, emailHref);
    const emailText = links.email && links.email.querySelector('[data-store-email-text]');
    if (emailText) emailText.textContent = emailHref && store.contact && store.contact.email ? store.contact.email : '';

    const appointment = card.querySelector('[data-bb-appointment]');
    if (appointment) {
      if (store.name) appointment.setAttribute('data-store-name', store.name);
      else appointment.removeAttribute('data-store-name');
    }
    const select = card.querySelector('[data-store-select]');
    if (select) {
      const template = select.getAttribute('data-store-select-label-template');
      if (template) select.setAttribute('aria-label', template.replace('[store]', store.name || generic));
      select.hidden = !store.coordinates;
    }
  }

  /* ---- component --------------------------------------------------------------------------------------------- */
  class BBStoreLocator extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-locator-list]');
      this.form = this.querySelector('[data-locator-form]');
      this.input = this.querySelector('[data-locator-input]');
      this.status = this.querySelector('[data-locator-status]');
      this.geoBtn = this.querySelector('[data-locator-geo]');
      this.map = this.querySelector('[data-locator-map]');
      this.mapDetails = this.querySelector('[data-locator-map-details]');
      this.workspace = this.querySelector('[data-locator-workspace]');
      this.cards = Array.from(this.querySelectorAll('.bb-store-card'));
      this.context = urlContext();
      this.uniqueShowroomHandoff = !!this.context.productGid && uniqueShowroomHandoff();
      this.seq = 0;
      this.unmatched = [];
      this.selected = null;
      if (!this.form) return;
      /* Card ↔ marker selection (both directions) and the mobile list/map switch — presentation only. */
      this.addEventListener('click', (e) => {
        const sel = e.target.closest('[data-store-select]');
        if (sel) { this.select(sel.getAttribute('data-store-select'), true); this.setView('map'); return; }
        const tab = e.target.closest('[data-locator-view]');
        if (tab) this.setView(tab.getAttribute('data-locator-view'));
      });
      if (this.map) {
        this.map.addEventListener('bb-map:select', (e) => this.select(e.detail.slug, false));
        this.map.addEventListener('bb-map:error', () => this.mapOff());
        if (this.mapDetails) this.mapDetails.addEventListener('toggle', () => {
          if (this.mapDetails.open && typeof this.map.show === 'function') this.map.show();
        });
      } else this.mapOff();
      BB.track('store_locator_view', { source_surface: 'store_locator', product_context: this.context.productGid ? 'model' : (this.context.brand ? 'brand' : 'none') }, 'view');
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.search(this.input.value.trim()); });
      if ('geolocation' in navigator && this.geoBtn) {
        this.geoBtn.hidden = false;
        this.geoBtn.addEventListener('click', () => this.locate());
      }
      this.list.addEventListener('click', (e) => {
        const cta = e.target.closest('[data-bb-appointment]');
        const card = e.target.closest('.bb-store-card');
        if (cta && card) BB.track('store_selected', { store_slug: card.getAttribute('data-store-slug'), via: 'appointment' });
      });
      /* Product context from a PDP handoff is forwarded to every appointment CTA on the page (contract keys product_gid / brand). */
      if (this.context.productGid || this.context.brand) {
        this.querySelectorAll('[data-bb-appointment]').forEach((btn) => {
          if (this.context.productGid) btn.setAttribute('data-product-gid', this.context.productGid);
          if (this.context.brand) btn.setAttribute('data-brand', this.context.brand);
        });
      }
      if (this.uniqueShowroomHandoff) {
        const fallbackUrl = this.getAttribute('data-unverified-booking-url') || '';
        const fallbackLabel = this.getAttribute('data-unverified-booking-label') || '';
        this.cards.forEach((card) => {
          BB.showroom.deferBooking(card.querySelector('[data-bb-appointment]'), fallbackUrl, fallbackLabel);
        });
      }
      this.enrich();
    }

    /* Initial store-list enrichment: BB.stores owns fetch/cache/normalization; P8 evidence interpretation remains BB.showroom. */
    enrich() {
      const seq = this.seq;
      this.cards.forEach((card) => card.setAttribute('data-store-source', 'loading'));
      return BB.stores.fetchList(this.context).then((result) => {
        if (!result || result.state !== BB.stores.states.PLATFORM) {
          if (seq !== this.seq) return result;
          const state = result && result.state === BB.stores.states.UNLISTED ? BB.stores.states.UNLISTED : BB.stores.states.UNAVAILABLE;
          this.cards.forEach((card) => applyAuthorityState(card, state));
          this.plot([]);
          return result;
        }

        const bySlug = new Map(result.stores.map((store) => [store.slug, store]));
        this.cards.forEach((card) => {
          const store = bySlug.get(card.getAttribute('data-store-slug'));
          if (store) {
            if (seq === this.seq || card.getAttribute('data-store-source') === 'loading') this.applyStore(card, store);
          } else if (seq === this.seq || card.getAttribute('data-store-source') === 'loading') {
            applyAuthorityState(card, BB.stores.states.UNLISTED);
          }
        });
        if (seq === this.seq) {
          if (this.uniqueShowroomHandoff) {
            const selection = BB.showroom.selectStores(result.stores, true);
            if (selection.exact.length === 1) {
              const store = selection.exact[0];
              const card = this.cards.find((candidate) => candidate.getAttribute('data-store-slug') === store.slug);
              if (card) BB.showroom.activateBooking(card.querySelector('[data-bb-appointment-pending]'), store);
            }
          }
          this.rankByEvidence();
          this.plot(result.stores);
        }
        return result;
      }).catch(() => {
        if (seq !== this.seq) return { state: BB.stores.states.UNAVAILABLE, stores: [] };
        this.cards.forEach((card) => applyAuthorityState(card, BB.stores.states.UNAVAILABLE));
        this.plot([]);
        return { state: BB.stores.states.UNAVAILABLE, stores: [] };
      });
    }
    /* Markers only for stores that have an SSR card (platform coordinates, theme never stores them). */
    plot(stores) {
      if (!this.map || typeof this.map.setStores !== 'function') return;
      const points = stores.filter((s) => this.cardFor(s) && s.coordinates && typeof s.coordinates.latitude === 'number' && typeof s.coordinates.longitude === 'number')
        .map((s) => {
          const card = this.cardFor(s);
          const title = card && card.querySelector('[data-store-name]');
          return { slug: s.slug, name: s.name || (title ? title.textContent : ''), lat: s.coordinates.latitude, lng: s.coordinates.longitude };
        });
      this.map.setStores(points);
    }
    /* Selection state: card (aria-current, scrolled into view) + marker. `pan` = card-initiated → map pans to it. */
    select(slug, pan) {
      this.selected = slug;
      this.cards.forEach((c) => {
        const on = c.getAttribute('data-store-slug') === slug;
        c.classList.toggle('is-selected', on);
        if (on) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
        const btn = c.querySelector('[data-store-select]');
        if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.map && typeof this.map.select === 'function') this.map.select(slug, pan);
      this.renderSelected(slug);
      if (!pan) { const card = this.cards.find((c) => c.getAttribute('data-store-slug') === slug); if (card && this.workspace && this.workspace.getAttribute('data-view') !== 'map' && !(this.mapDetails && this.mapDetails.open)) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
    }
    /* Mobile map view: mirror the selected card's essentials (name, address, status, CTAs) under the map — cloned from the
       SSR card, so copy and booking context stay single-sourced. */
    renderSelected(slug) {
      const box = this.querySelector('[data-locator-selected]');
      const card = this.cards.find((c) => c.getAttribute('data-store-slug') === slug);
      if (!box) return;
      if (!card) { box.hidden = true; box.innerHTML = ''; return; }
      box.innerHTML = '';
      const add = (node) => { if (node) box.appendChild(node); };
      const title = document.createElement('p'); title.className = 'bb-store-finder__selected-title'; title.textContent = (card.querySelector('.bb-store-card__title') || {}).textContent || ''; add(title);
      const address = card.querySelector('.bb-store-card__address'); if (address) { const a = address.cloneNode(true); a.className = 'bb-store-finder__selected-address'; add(a); }
      const status = card.querySelector('[data-store-status]'); if (status && !status.hidden) { const st = status.cloneNode(true); st.removeAttribute('data-store-status'); add(st); }
      const primary = card.querySelector('.bb-store-card__primary'); if (primary) add(primary.cloneNode(true));
      const links = document.createElement('div'); links.className = 'bb-store-finder__selected-links';
      card.querySelectorAll('.bb-store-card__links a').forEach((a) => links.appendChild(a.cloneNode(true)));
      if (links.children.length) add(links);
      box.hidden = false;
    }
    /* No usable map (no key, blocked, load error): list-only layout, switch hidden, list view forced. */
    mapOff() {
      if (this.workspace) { this.workspace.setAttribute('data-map', 'off'); this.workspace.setAttribute('data-view', 'list'); }
      const sw = this.querySelector('[data-locator-switch]'); if (sw) sw.hidden = true;
      if (this.mapDetails) { this.mapDetails.open = false; this.mapDetails.hidden = true; }
    }
    /* Mobile list/map switch (desktop shows both; CSS ignores the attribute there). Map initialises on first show. */
    setView(view) {
      if (!this.workspace) return;
      this.workspace.setAttribute('data-view', view);
      this.querySelectorAll('[data-locator-view]').forEach((b) => b.setAttribute('aria-pressed', b.getAttribute('data-locator-view') === view ? 'true' : 'false'));
      if (view === 'map' && this.map && typeof this.map.show === 'function') this.map.show();
    }
    cardFor(store) {
      if (!store || typeof store !== 'object' || typeof store.slug !== 'string') return null;
      return this.cards.find((c) => c.getAttribute('data-store-slug') === store.slug) || null;
    }
    /* Per-card presentation consumes only a normalized BB.stores store. P9 distance stays separate metadata. */
    applyStore(card, store, distanceKm) {
      applyOperationalCard(card, store);
      this.applyEvidence(card, BB.showroom.normalize(store));
      const dist = card.querySelector('[data-store-distance]');
      if (dist && distanceKm !== undefined) {
        const text = formatDistance(distanceKm);
        if (text) { dist.textContent = text; dist.hidden = false; } else { dist.textContent = ''; dist.hidden = true; }
      }
    }
    /* Presence label strictly from the platform's evidence object. exact_product → model-level label (available_for_demo
       gets its own, weaker wording); brand_only → brand-level label with the platform's brand name; no_specific_match /
       unknown → no label at all (no evidence is never "not present"). */
    applyEvidence(card, ev) {
      const match = card.querySelector('[data-store-match]');
      if (!match || !ev.positive) return;
      let text = '';
      if (ev.level === 'exact_product' && ev.status === 'available_for_demo') text = BB.t('matchDemo');
      else if (ev.level === 'exact_product' && ev.status === 'on_display') text = BB.t('matchDisplay');
      else if (ev.level === 'exact_product' && ev.status === 'in_assortment') text = BB.t('matchAssortment');
      else text = ev.brand ? BB.t('matchBrandNamed', { brand: ev.brand.name }) : BB.t('matchBrand');
      match.textContent = text;
      match.classList.add('is-' + ev.level);
      match.hidden = false;
      card.setAttribute('data-store-match', ev.level);
    }
    /* Only for the initial GET enrichment (no location known): evidence class first, SSR order within a class. Every
       search/geo result is server-ranked and is never re-sorted here. */
    rankByEvidence() {
      const rank = (c) => BB.showroom.rank(c.getAttribute('data-store-match'));
      if (!this.cards.some((c) => rank(c) < 2)) return;
      this.order(this.cards.slice().sort((a, b) => rank(a) - rank(b)));
    }

    /* Text search → P9 (query mode). Query text lives in the POST body only. */
    search(q) {
      if (!q) { this.reset(); return; }
      const queryType = /^\d/.test(q) ? 'postal_code' : 'city';
      this.request({ query: q }, queryType);
    }
    /* Browser geolocation: only after the explicit click; coordinates go to the POST body and nowhere else. */
    locate() {
      if (!('geolocation' in navigator)) { this.setStatus(BB.t('locationUnavailable')); return; }
      this.setStatus(BB.t('locating'));
      navigator.geolocation.getCurrentPosition(
        (pos) => this.request({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }, 'geolocation'),
        () => { this.setStatus(BB.t('locationDenied')); },
        { timeout: 8000, maximumAge: 300000 }
      );
    }
    request(locationInput, queryType) {
      const seq = ++this.seq;
      const url = BB.platformUrl(SEARCH_PATH);
      if (!url) { this.setStatus(BB.t('searchUnavailable')); return Promise.resolve(); }
      this.setStatus(BB.t('searching'));
      this.list.setAttribute('aria-busy', 'true');
      return BB.fetchJSON(url, { json: buildBody(locationInput, this.context) }).then((data) => {
        if (seq !== this.seq) return; /* stale: a newer search is in flight or answered */
        if (!data || !Array.isArray(data.stores)) throw new Error('malformed-response');
        this.applyResults(data.stores, queryType);
      }).catch((err) => {
        if (seq !== this.seq) return;
        const outcome = classifyError(err);
        this.cards.forEach((c) => { const d = c.querySelector('[data-store-distance]'); if (d) d.hidden = true; });
        if (outcome === 'location_unresolved' || outcome === 'location_required') { this.setStatus(BB.t('locationUnresolved')); BB.track('store_locator_search', { query_type: queryType, results_count: 0 }); }
        else if (outcome === 'rate_limited') this.setStatus(BB.t('rateLimited'));
        else this.setStatus(BB.t('searchUnavailable'));
      }).then(() => { if (seq === this.seq) this.list.removeAttribute('aria-busy'); });
    }
    /* P9 remains authoritative for result order + distance. BB.stores only normalizes the store payload for presentation. */
    applyResults(stores, queryType) {
      const result = orderBySlugs(this.cards, stores);
      this.unmatched = result.unmatchedStores;
      const normalized = stores.map((raw) => ({ raw: raw, store: BB.stores.normalize(raw) })).filter((record) => record.store);
      normalized.forEach((record) => { const card = this.cardFor(record.store); if (card) this.applyStore(card, record.store, record.raw.distanceKm); });
      this.order(result.ordered);
      this.plot(normalized.map((record) => record.store));
      const matchedSlugs = result.ordered.slice(0, result.matched).map((c) => c.getAttribute('data-store-slug'));
      if (this.map && typeof this.map.fit === 'function' && matchedSlugs.length) this.map.fit(matchedSlugs);
      if (matchedSlugs.length) this.select(matchedSlugs[0], false);
      this.setStatus(result.matched ? BB.t('resultsSorted') : BB.t('storesNoResults'));
      BB.track('store_locator_search', { query_type: queryType, results_count: result.matched });
    }
    setStatus(text) { if (this.status) this.status.textContent = text || ''; }
    /* DOM re-ordering keeps keyboard focus where it was. */
    order(cards) {
      const active = document.activeElement;
      cards.forEach((c) => this.list.appendChild(c.closest('li') || c));
      if (active && this.list.contains(active) && document.activeElement !== active) { try { active.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
    }
    reset() { this.seq += 1; this.list.removeAttribute('aria-busy'); this.order(this.cards); this.cards.forEach((c) => { const d = c.querySelector('[data-store-distance]'); if (d) d.hidden = true; }); this.setStatus(''); if (this.map && typeof this.map.fitAll === 'function') this.map.fitAll(); }
  }
  if (!customElements.get('bb-store-locator')) customElements.define('bb-store-locator', BBStoreLocator);
  BB.locator = { buildBody: buildBody, classifyError: classifyError, orderBySlugs: orderBySlugs, formatDistance: formatDistance, openState: openState, applyOpenState: applyOpenState, SEARCH_PATH: SEARCH_PATH };
})();
